import dayjs from "dayjs";
import { getTwoDigitNo, sortTodoList } from "utils";

/**
 * Maps `getTodoTemplates` results into the shape the mobile AssessmentCard
 * needs. Replicates the desktop new/expiresSoon computation (Home/TodoList) and
 * the label logic (TemplateList.TemplateCard) so mobile matches desktop exactly.
 */

// TemplateList.formateAverageTime
function formatTime(time) {
  if (!time) return null;
  return time < 60
    ? `${getTwoDigitNo(time)} Secs`
    : `${getTwoDigitNo(Math.floor(time / 60))} Mins`;
}

// Home/TodoList: sets `new` and `expiresSoon` flags on each template.
function computeFlags(template) {
  const { publishOn, expiresOn, attempts } = template;

  template.new = false;
  template.expiresSoon = false;

  if (dayjs(expiresOn).year() === 9999) {
    template.new = !attempts;
    template.expiresSoon = false;
    return;
  }

  const startDate = dayjs(publishOn);
  const endDate = dayjs(expiresOn);
  const now = dayjs();
  const percentageValue = 0.16;

  const timeElapsedInMinutes = endDate.diff(startDate, "minute");
  const thresholdDate = startDate.add(percentageValue * timeElapsedInMinutes, "minute");
  const thresholdDateForExpire = endDate.subtract(
    percentageValue * timeElapsedInMinutes,
    "minute"
  );

  template.new = now.isBefore(thresholdDate) && !attempts;
  template.thresholdDateForExpire = thresholdDateForExpire;

  if (now.isSame(endDate, "day")) {
    template.expiresSoon = now.isAfter(thresholdDateForExpire);
  } else if (now.isAfter(thresholdDateForExpire)) {
    template.expiresSoon = true;
  }
}

// TemplateCard expiry label.
function expiryLabel(template) {
  const { expiresOn } = template;
  if (!expiresOn) return null;
  if (dayjs(expiresOn).year() === 9999) return "Never Expires";
  return `Expires on: ${dayjs(expiresOn).format("DD/MM/YYYY")}`;
}

export function mapTodoTemplates(templates = []) {
  // Work on shallow copies so we never mutate the caller's raw templates.
  const withFlags = templates.map((t) => {
    const copy = { ...t };
    computeFlags(copy);
    return copy;
  });
  withFlags.sort(sortTodoList);

  return withFlags.map((t) => {
    const isPersonalized = t.type === "personalized";

    return {
      id: t._id,
      type: t.type, // lowercase — used for filtering
      typeLabel: isPersonalized ? "Mock Interview" : t.type,
      title: isPersonalized ? t?.data?.INTERVIEW_DETAILS?.name : t?.name,
      isNew: !!t.new,
      expiresSoon: !!t.expiresSoon,
      expiry: expiryLabel(t),
      attemptsAllowed:
        t.maxAllowedAttempts === -1
          ? "Unlimited Attempts"
          : `Attempts Left: ${getTwoDigitNo(
              Math.max((t.maxAllowedAttempts || 0) - (t.attempts || 0), 0)
            )}`,
      questionsLabel:
        t.type === "follow_up"
          ? "05 Questions"
          : `${getTwoDigitNo(t?.metadata?.numberOfQuestions || 0)} Questions`,
      timeLabel:
        t.type === "follow_up" ? "5-10 mins" : formatTime(t?.metadata?.testTime),
      attemptsLabel: !t.attempts
        ? "No Attempts"
        : `${getTwoDigitNo(t.attempts)}x Attempted`,
      // Raw template (with computed flags) — the instructions modal reads
      // metadata.testTime, expiresOn, type and maxAllowedAttempts from it.
      raw: t,
    };
  });
}
